import { create } from "zustand";
import type { Node, Edge } from "@xyflow/react";
import { defaultSecurity } from "../data/security-defaults.js";

let agentSeq = 0;

/** Create a fresh builder agent node with sane, valid defaults. */
export function createAgentNode(opts: {
  backend: string;
  model?: string;
  label?: string;
  position: { x: number; y: number };
}): Node {
  agentSeq += 1;
  const id = `agent-${agentSeq}`;
  const label = opts.label ?? "New Agent";
  return {
    id,
    type: "agent",
    position: opts.position,
    data: {
      label,
      name: label,
      agentId: id,
      backend: opts.backend,
      model: opts.model,
      status: "idle",
      description: "",
      identity: { persona: "", instructions: "" },
      capabilities: [],
      security: defaultSecurity("semi-trusted"),
      isEntryPoint: false,
      isExitPoint: false,
      orientation: "vertical",
    },
  };
}

export type BuilderEdgeType = "delegates-to" | "reports-to" | "peers-with" | "pipes-to";

interface CanvasStore {
  nodes: Node[];
  edges: Edge[];
  selectedNodeId: string | null;
  /** The edge type new connections get in the builder. */
  defaultEdgeType: BuilderEdgeType;
  /** Which persisted topology the builder is currently loaded from (null = blank build). */
  loadedTopologyId: string | null;

  setNodes: (nodes: Node[]) => void;
  setDefaultEdgeType: (type: BuilderEdgeType) => void;
  setLoadedTopologyId: (id: string | null) => void;
  setEdges: (edges: Edge[]) => void;
  selectNode: (id: string | null) => void;
  addNode: (node: Node) => void;
  updateNode: (id: string, data: Partial<Node["data"]>) => void;
  removeNode: (id: string) => void;
  addEdge: (edge: Edge) => void;
  removeEdge: (id: string) => void;
  /** Mark one node as the single entry point (clears the flag on the rest). */
  setEntryPoint: (id: string) => void;
  /** Toggle whether a node is an exit point. */
  toggleExitPoint: (id: string) => void;
  /** Clear the canvas to a blank slate (used when starting a new build). */
  reset: () => void;
}

export const useCanvasStore = create<CanvasStore>((set) => ({
  nodes: [],
  edges: [],
  selectedNodeId: null,
  defaultEdgeType: "delegates-to",
  loadedTopologyId: null,

  setNodes: (nodes) => set({ nodes }),
  setDefaultEdgeType: (type) => set({ defaultEdgeType: type }),
  setLoadedTopologyId: (id) => set({ loadedTopologyId: id }),
  setEdges: (edges) => set({ edges }),
  selectNode: (id) => set({ selectedNodeId: id }),
  addNode: (node) => set((s) => ({ nodes: [...s.nodes, node] })),
  updateNode: (id, data) =>
    set((s) => ({
      nodes: s.nodes.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, ...data } } : n,
      ),
    })),
  removeNode: (id) =>
    set((s) => ({
      nodes: s.nodes.filter((n) => n.id !== id),
      edges: s.edges.filter((e) => e.source !== id && e.target !== id),
    })),
  addEdge: (edge) => set((s) => ({ edges: [...s.edges, edge] })),
  removeEdge: (id) =>
    set((s) => ({ edges: s.edges.filter((e) => e.id !== id) })),
  setEntryPoint: (id) =>
    set((s) => ({
      nodes: s.nodes.map((n) => ({
        ...n,
        data: { ...n.data, isEntryPoint: n.id === id },
      })),
    })),
  toggleExitPoint: (id) =>
    set((s) => ({
      nodes: s.nodes.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, isExitPoint: !n.data?.isExitPoint } } : n,
      ),
    })),
  reset: () => set({ nodes: [], edges: [], selectedNodeId: null, loadedTopologyId: null }),
}));
